import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {
    DxAccordionModule,
    DxButtonModule, DxColorBoxModule,
    DxFileUploaderModule,
    DxFormModule, DxLoadIndicatorModule,
    DxPopupModule, DxSelectBoxModule, DxTagBoxModule,
    DxTextBoxModule, DxValidatorModule
} from 'devextreme-angular';
import {ThemeService} from '../../services/theme-service';
import {Color, TenantOption, ThemeDto} from '../../model/Theme';
import {finalize, of, switchMap} from 'rxjs';
import {ColorUtils} from '../../utils/ColorUtils';
import {NgStyle} from '@angular/common';
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';
import type { ValueChangedEvent } from 'devextreme/ui/color_box';
import {ValueChangedEvent as SelectValueChangedEvent} from 'devextreme/ui/select_box'
import {ReportType} from "../../model/ReportType";
import * as fileSaver from "file-saver";
import {TenantService} from "../../services/tenant-service";

@Component({
  selector: 'add-edit-component',
    imports: [
        DxFormModule,
        DxFileUploaderModule,
        DxPopupModule,
        DxButtonModule,
        DxAccordionModule,
        DxTextBoxModule,
        DxValidatorModule,
        DxColorBoxModule,
        NgStyle,
        DxLoadIndicatorModule,
        DxSelectBoxModule,
        DxTagBoxModule
    ],
  templateUrl: './add-edit-component.html',
  styleUrl: './add-edit-component.scss'
})
export class AddEditComponent implements OnInit, OnDestroy {

  @Input() theme: ThemeDto = {} as ThemeDto;
  @Input() visible!: boolean;
  @Output() visibleChange = new EventEmitter<boolean>();

  @Output() onFinished = new EventEmitter<boolean>();

  _theme: ThemeDto = {} as ThemeDto;

  files: File[] = [];
  defaultChartColors: Color[] = [
        { red: 0, green: 94, blue: 166, alpha: 1 },     // #005ea6
        { red: 46, green: 175, blue: 229, alpha: 1 },   // #2eafe5
        { red: 0, green: 60, blue: 110, alpha: 1 },     // #003c6e
        { red: 134, green: 155, blue: 166, alpha: 1 },  // #869ba6
        { red: 237, green: 28, blue: 36, alpha: 1 },    // #ed1c24
        { red: 0, green: 173, blue: 171, alpha: 1 },    // #00adab
        { red: 139, green: 107, blue: 175, alpha: 1 },  // #8b6baf
        { red: 110, green: 174, blue: 68, alpha: 1 },   // #6eae44
        { red: 52, green: 142, blue: 205, alpha: 1 },   // #348ecd
        { red: 255, green: 194, blue: 14, alpha: 1 },   // #ffc20e
    ];
  newField: {name:string;value:string} = {name:'', value:''};
  newColor: Color = {} as Color;
  errorMessages:{chartColor:string, mdfmColor:string, fieldName:string} = {chartColor:'', mdfmColor:'', fieldName:''};
  showIndicator: boolean = false;
  chartColorValue: string = "";
  previewPagePopupVisible: boolean = false;
  enlargedPreviewPageUrl: string = '';
  enlargedPreviewPageName: string = '';
  draggedIndex: number | null = null;
  dragOverIndex: number | null = null;
  previewPageUrls: Map<string, string> = new Map();
  addPreviewPageDialogVisible: boolean = false;
  newPreviewPageName: string = '';
  newPreviewPageFile: File | null = null;
  previewPageNameError: string = '';
  canAddPreviewPage: boolean = false;

  reportTypes = Object.values(ReportType);
  tenants: TenantOption[] = [];

  constructor(private themeService: ThemeService,
              private tenantService: TenantService
  ) {}

  ngOnInit() {
    this._theme = JSON.parse(JSON.stringify(this.theme));
    if (!this.theme.id) {
        this._theme.type = this.reportTypes[0];
        this._theme.chartPalette = { colors: this.defaultChartColors };
    } else {
        this.loadAllPreviewPages();
    }
    this.tenantService.getTenantList()
      .subscribe(tenantList => {
          this.tenants = [{displayName: "ALL", value: "ALL"}, ...Object.values(tenantList)];
      });
  }

  ngOnDestroy() {
    this.previewPageUrls.forEach((url, pageId) => {
      if (url && url !== 'loading' && !url.endsWith('.svg')) {
        URL.revokeObjectURL(url);
      }
    });
    this.previewPageUrls.clear();
  }

  selectType({value}: SelectValueChangedEvent) {
    this._theme.type = value;
  }

  onTenantChange(e: any) {
      if (!this._theme) return;

      const newValue: string[] = e.value || [];
      const previousValue: string[] = e.previousValue || [];

      // if "ALL" was just added, keep only "ALL"
      if (newValue.includes("ALL") && !previousValue.includes("ALL")) {
          this._theme.tenant = ["ALL"];
      }
      // if another option was added while "ALL" is selected, remove "ALL"
      else if (newValue.includes("ALL") && newValue.length > 1) {
          this._theme.tenant = newValue.filter((t: string) => t !== "ALL");
      }
      else {
          this._theme.tenant = newValue;
      }
  }

  send(e: SubmitEvent) {
    e.preventDefault();

    if ((this._theme.chartPalette?.colors?.length ?? 0) < 10) return

    this.showIndicator = true;
    if(this.theme?.id){
      this.themeService.update(this._theme)
        .pipe(
          switchMap(res => {
            if (this.files[0]) {
              const formData = new FormData();
              formData.append("themeFile", this.files[0], this.files[0].name);

              notify("Processing. . .", "success", 2000)
              return this.themeService.updateFile(this.theme.id, formData);
            } else {
              notify("Theme updated.", "success", 2000);
              return of(res)
            }
          }),
          finalize(() => {
            this.showIndicator = false;
          }),
        )
        .subscribe(res => {
            Object.assign(this.theme, res);
            this.onFinished.emit(true);
      });
    } else {
      const formData = new FormData();
      formData.append("theme", JSON.stringify(this._theme));

      this.themeService.create(formData)
        .pipe(
          switchMap(res => {
            notify("Processing. . .", "success", 2000)

            const formUpload = new FormData();
            if(this.files[0]){
              formUpload.append("themeFile", this.files[0], this.files[0].name);
              return this.themeService.updateFile(res.id, formUpload);
            }
            else {
              notify("Theme created.", "success", 2000)
              return of(res);
            }

          }),
          finalize(() => this.showIndicator = false)
        )
        .subscribe(response => {
          this.onFinished.emit(true);
        });
    }
  }

    onVisibleChange(newValue: boolean) {
    this.visible = newValue;
    this.visibleChange.emit(this.visible);
  }

  addNewChartPalette(e: ValueChangedEvent) {
    this.errorMessages.chartColor = "";


    if(!e.value)
      return;

    const rgb = ColorUtils.hexToRgb(e.value);
    if (rgb) {
      this.newColor = rgb;
    }

    e.component.clear();

    this._theme.chartPalette.colors.push(this.newColor);
    this.newColor = {} as Color;
  }

  removeChartPalette(index: number) {
    this._theme.chartPalette.colors.splice(index, 1);
  }

  downloadFile() {
        this.showIndicator = true;
        this.themeService.getInddFile(this._theme.id)
            .pipe(finalize(() => this.showIndicator = false))
            .subscribe(
                (data: any) => {
                    const blob = new Blob([data], { type: "application/x-indesign" });
                    fileSaver.saveAs(blob, this._theme.fileName);
                },
                (error) => {
                    notify(error.message);
                }
            );
    }

  colorToHex(color: Color) {
    return ColorUtils.rgbToHex(color);
  }

  getPreviewPageUrl(pageId: string): string {
    return this.previewPageUrls.get(pageId) || 'assets/icons/image.svg';
  }

  loadAllPreviewPages(): void {
    if (!this._theme?.previewPages) return;
    for (const page of this._theme.previewPages) {
      this.loadPreviewPageImage(page.previewId);
    }
  }

  loadPreviewPageImage(pageId: string): void {
    if (this.previewPageUrls.has(pageId)) return;
    this.previewPageUrls.set(pageId, 'loading');
    this.themeService.getPreviewPage(this._theme.id, pageId).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        this.previewPageUrls.set(pageId, url);
      },
      error: () => {
        this.previewPageUrls.set(pageId, 'assets/icons/image.svg');
      }
    });
  }

  openPreviewPage(pageId: string, pageName: string) {
    this.enlargedPreviewPageUrl = this.previewPageUrls.get(pageId) || 'assets/icons/image.svg';
    this.enlargedPreviewPageName = pageName;
    this.previewPagePopupVisible = true;
  }

  closePreviewPagePopup() {
    this.previewPagePopupVisible = false;
  }

  openAddPreviewPageDialog() {
    this.newPreviewPageName = '';
    this.newPreviewPageFile = null;
    this.previewPageNameError = '';
    this.addPreviewPageDialogVisible = true;
  }

  closeAddPreviewPageDialog() {
    this.addPreviewPageDialogVisible = false;
    this.newPreviewPageName = '';
    this.newPreviewPageFile = null;
    this.previewPageNameError = '';
    this.canAddPreviewPage = false;
  }

  validatePreviewPageName() {
    const name = this.newPreviewPageName.trim();
    if (!name) {
      this.previewPageNameError = '';
      this.updateCanAddPreviewPage();
      return;
    }
    const existingNames = this._theme.previewPages?.map(p => p.name.toLowerCase()) || [];
    if (existingNames.includes(name.toLowerCase())) {
      this.previewPageNameError = 'A page with this name already exists.';
    } else {
      this.previewPageNameError = '';
    }
    this.updateCanAddPreviewPage();
  }

  onNewPreviewPageFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.newPreviewPageFile = input.files[0];
    }
    input.value = '';
    this.updateCanAddPreviewPage();
  }

  private updateCanAddPreviewPage() {
    this.canAddPreviewPage = !!(this.newPreviewPageName.trim() && this.newPreviewPageFile && !this.previewPageNameError);
  }

  addPreviewPage() {
    if (!this.canAddPreviewPage) return;
    
    this.showIndicator = true;
    this.addPreviewPageDialogVisible = false;
    
    this.themeService.addPreviewPage(this._theme.id, this.newPreviewPageFile!, this.newPreviewPageName.trim())
      .pipe(finalize(() => this.showIndicator = false))
      .subscribe({
        next: (res) => {
          this._theme.previewPages = res.previewPages;
          Object.assign(this.theme, res);
          this.loadPreviewPageImage(res.previewPages[res.previewPages.length - 1].previewId);
          notify("Preview page added successfully.", "success", 2000);
        },
        error: (err) => {
          notify("Failed to add preview page. Only PNG files are allowed.", "error", 2000);
        }
      });
    
    this.newPreviewPageName = '';
    this.newPreviewPageFile = null;
  }

  deletePreviewPage(pageId: string, event: { event?: Event }) {
    event.event?.stopPropagation();
    const page = this._theme.previewPages?.find(p => p.previewId === pageId);
    const pageName = page?.name || 'this page';
    
    confirm(`Are you sure you want to delete "${pageName}"?`, "Confirm Deletion").then((confirmed) => {
      if (confirmed) {
        this.showIndicator = true;
        this.themeService.deletePreviewPage(this._theme.id, pageId)
          .pipe(finalize(() => this.showIndicator = false))
          .subscribe({
            next: (res) => {
              const blobUrl = this.previewPageUrls.get(pageId);
              if (blobUrl && blobUrl !== 'loading' && !blobUrl.endsWith('.svg')) {
                URL.revokeObjectURL(blobUrl);
              }
              this.previewPageUrls.delete(pageId);
              this._theme.previewPages = res.previewPages;
              Object.assign(this.theme, res);
              notify("Preview page deleted.", "success", 2000);
            },
            error: (err) => {
              notify("Failed to delete preview page.", "error", 2000);
            }
          });
      }
    });
  }

  downloadPreviewPage(pageId: string, pageName: string, event: { event?: Event }) {
    event.event?.stopPropagation();
    const fileName = `${this._theme.name}_${pageName}.png`;
    const blobUrl = this.previewPageUrls.get(pageId);
    if (blobUrl && blobUrl !== 'loading' && !blobUrl.endsWith('.svg')) {
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.click();
    } else {
      this.themeService.getPreviewPage(this._theme.id, pageId).subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          link.click();
          URL.revokeObjectURL(url);
        },
        error: () => {
          notify("Failed to download preview page.", "error", 2000);
        }
      });
    }
  }

  onDragStart(event: DragEvent, index: number) {
    this.draggedIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', index.toString());
    }
  }

  onDragEnd(event: DragEvent) {
    this.draggedIndex = null;
    this.dragOverIndex = null;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  onDragOverItem(event: DragEvent, index: number) {
    event.preventDefault();
    if (this.draggedIndex !== null && this.draggedIndex !== index) {
      this.dragOverIndex = index;
    }
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    if (this.draggedIndex === null || this.dragOverIndex === null) return;
    if (this.draggedIndex === this.dragOverIndex) return;

    const pages = [...this._theme.previewPages];
    const [draggedItem] = pages.splice(this.draggedIndex, 1);
    pages.splice(this.dragOverIndex, 0, draggedItem);

    const orderedIds = pages.map(p => p.previewId);

    this.showIndicator = true;
    this.themeService.reorderPreviewPages(this._theme.id, orderedIds)
      .pipe(finalize(() => {
        this.showIndicator = false;
        this.draggedIndex = null;
        this.dragOverIndex = null;
      }))
      .subscribe({
        next: (res) => {
          this._theme.previewPages = res.previewPages;
          Object.assign(this.theme, res);
        },
        error: (err) => {
          notify("Failed to reorder preview pages.", "error", 2000);
        }
      });
  }

}
